import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Link, useLocation } from "wouter"
import {
  BarChart3,
  ClipboardCheck,
  Building,
  FileText,
  Lightbulb,
  Copy,
  Plug,
  Settings,
  Users,
  HelpCircle,
  Shield,
  CreditCard,
  DollarSign,
  HardDrive,
  Kanban,
  FileBarChart,
  ClipboardList,
  type LucideIcon,
} from "lucide-react"
import { useState } from "react"
import { useAuth } from "@/hooks/useAuth"
import { hasAnyRole } from "@/lib/authUtils"
import { useQuery } from "@tanstack/react-query"
import { useOnboarding } from "@/hooks/use-onboarding"
import { pendingReviewLinkTarget } from "@shared/pendingReviewLink"

interface SidebarProps {
  className?: string
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}

// ---------------------------------------------------------------------------
// Navigation model — phase-aware, role-aware, additive hierarchy.
//
// Roles are additive: owner ⊃ admin ⊃ vcio ⊃ alignment_engineer. A group or
// item with no `roles` is visible to everyone; one with `roles` is visible to
// any user whose role is listed. Higher roles therefore see the UNION of their
// own sections plus every lower section, emphasising their primary job without
// hiding permitted lower-level work.
//
// Naming collisions resolved:
//   /qbrs              → "Business Reviews" (the QBR deliverables)
//   /quarterly-reviews → "Review Cycles"    (the recurring review process)
//   /reports           → "Reports"          (report library; /budget-reports redirects here)
// ---------------------------------------------------------------------------

const VCIO_PLUS = ["vcio", "admin", "owner"]
const ADMIN_PLUS = ["admin", "owner"]
const OWNER_ONLY = ["owner"]

interface NavItem {
  title: string
  href: string
  icon: LucideIcon
  onboardingId: string
  roles?: string[]
  /** Renders contextual count badges (e.g. assessments awaiting review). */
  badgeKey?: "assessments"
  /** Tooltip label shown when collapsed, if it differs from `title`. */
  collapsedLabel?: string
}

interface NavGroup {
  title: string
  roles?: string[]
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "Overview",
    items: [
      { title: "Dashboard", href: "/", icon: BarChart3, onboardingId: "nav-dashboard" },
    ],
  },
  {
    title: "Clients",
    items: [
      { title: "Customers", href: "/customers", icon: Building, onboardingId: "nav-customers" },
      { title: "Assets", href: "/assets", icon: HardDrive, onboardingId: "nav-assets" },
    ],
  },
  {
    title: "Alignment",
    items: [
      {
        title: "Assessments",
        href: "/assessments",
        icon: ClipboardCheck,
        onboardingId: "nav-assessments",
        badgeKey: "assessments",
      },
    ],
  },
  {
    // The vCIO delivery pipeline, surfaced top-to-bottom as one connected
    // sequence: review → recommendations → budget → business review → present.
    title: "Delivery Pipeline",
    roles: VCIO_PLUS,
    items: [
      { title: "Review Schedule", href: "/review-schedule", icon: ClipboardList, onboardingId: "nav-quarterly-reviews" },
      { title: "Recommendations", href: "/recommendations", icon: Lightbulb, onboardingId: "nav-recommendations" },
      { title: "Budget Planning", href: "/budget-planning", icon: DollarSign, onboardingId: "nav-budget-planning" },
      { title: "Business Reviews", href: "/qbrs", icon: FileText, onboardingId: "nav-qbrs" },
      { title: "Implementation", href: "/implementation-tracking", icon: Kanban, onboardingId: "nav-implementation" },
    ],
  },
  {
    title: "Insights",
    roles: VCIO_PLUS,
    items: [
      { title: "Reports", href: "/reports", icon: FileBarChart, onboardingId: "nav-reports" },
    ],
  },
  {
    title: "Resources",
    items: [
      { title: "Templates", href: "/templates", icon: Copy, onboardingId: "nav-templates" },
      { title: "Help & Tours", href: "/help", icon: HelpCircle, onboardingId: "nav-help" },
    ],
  },
  {
    title: "Administration",
    roles: ADMIN_PLUS,
    items: [
      { title: "User Management", href: "/users", icon: Users, onboardingId: "nav-users" },
      { title: "Integrations", href: "/integrations", icon: Plug, onboardingId: "nav-integrations" },
      { title: "Audit Logs", href: "/audit-logs", icon: Shield, onboardingId: "nav-audit-logs" },
      {
        title: "Billing",
        href: "/billing",
        icon: CreditCard,
        onboardingId: "nav-billing",
        roles: OWNER_ONLY,
        collapsedLabel: "Subscription Management",
      },
      {
        title: "Settings",
        href: "/settings",
        icon: Settings,
        onboardingId: "nav-settings",
        collapsedLabel: "Organization Settings",
      },
    ],
  },
]

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items)
const HREF_TO_ONBOARDING: Record<string, string> = Object.fromEntries(
  ALL_ITEMS.map((i) => [i.href, i.onboardingId]),
)

function visibleGroupsFor(user: any): NavGroup[] {
  return NAV_GROUPS
    .filter((g) => !g.roles || hasAnyRole(user, g.roles))
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.roles || hasAnyRole(user, it.roles)),
    }))
    .filter((g) => g.items.length > 0)
}

// Shared badge data for the Assessments item (vCIO+ only).
function useAssessmentBadges() {
  const { user } = useAuth()
  const enabled = hasAnyRole(user, VCIO_PLUS)

  const { data: assessments } = useQuery<any[]>({
    queryKey: ["/api/assessments"],
    enabled,
  })
  const { data: qbrs } = useQuery<any[]>({
    queryKey: ["/api/qbrs"],
    enabled,
  })

  const pendingReviewIds =
    assessments?.filter((a: any) => a.status === "pending_review").map((a: any) => a.id) || []

  const pendingReviewsCount = pendingReviewIds.length

  const approvedAssessmentsCount =
    assessments?.filter((a: any) => {
      if (a.status !== "approved") return false
      const assignedToQbr = qbrs?.some((q: any) => q.assessmentId === a.id)
      return !assignedToQbr
    }).length || 0

  return { enabled, pendingReviewsCount, pendingReviewIds, approvedAssessmentsCount }
}

interface BadgeData {
  enabled: boolean
  pendingReviewsCount: number
  pendingReviewIds: number[]
  approvedAssessmentsCount: number
}

interface NavLinkProps {
  item: NavItem
  active: boolean
  highlighted: boolean
  collapsed?: boolean
  mobile?: boolean
  badges: BadgeData
  onNavigate?: () => void
}

function NavLink({ item, active, highlighted, collapsed, mobile, badges, onNavigate }: NavLinkProps) {
  const Icon = item.icon
  const showBadges =
    item.badgeKey === "assessments" &&
    badges.enabled &&
    (badges.pendingReviewsCount > 0 || badges.approvedAssessmentsCount > 0)

  // Inline labels/badges show when expanded (desktop) or always (mobile).
  const showLabel = mobile || !collapsed

  const button = (
    <Button
      variant={active ? "default" : "ghost"}
      className={cn(
        mobile
          ? "w-full justify-start h-12 px-4 text-base"
          : cn(
              "h-10 w-full overflow-hidden whitespace-nowrap transition-all duration-300",
              collapsed ? "px-0 justify-center" : "justify-start px-3",
            ),
        active
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "text-foreground hover:bg-accent hover:text-accent-foreground",
        highlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background",
      )}
      data-onboarding={item.onboardingId}
      onClick={onNavigate}
    >
      <Icon className={cn("flex-shrink-0", mobile ? "h-5 w-5" : "h-4 w-4")} />
      {showLabel && (
        <>
          <span className="ml-3">{item.title}</span>
          {showBadges && (
            <span className="ml-auto flex gap-1 items-center">
              {badges.pendingReviewsCount > 0 && (
                <Link
                  href={pendingReviewLinkTarget(badges.pendingReviewIds)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {badges.pendingReviewsCount}
                  </span>
                </Link>
              )}
              {badges.approvedAssessmentsCount > 0 && (
                <span className="bg-green-600 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {badges.approvedAssessmentsCount}
                </span>
              )}
            </span>
          )}
        </>
      )}
    </Button>
  )

  const link = (
    <Link href={item.href}>
      {button}
    </Link>
  )

  // Collapsed desktop rail: show a tooltip with the label (and badge details).
  if (!mobile && collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="ml-2">
          <p>{item.collapsedLabel || item.title}</p>
          {showBadges && badges.pendingReviewsCount > 0 && (
            <p className="text-xs text-orange-300">
              {badges.pendingReviewsCount} awaiting your review
            </p>
          )}
          {showBadges && badges.approvedAssessmentsCount > 0 && (
            <p className="text-xs text-emerald-300">
              {badges.approvedAssessmentsCount} ready for Business Review
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return link
}

function useNavHighlight() {
  const [location] = useLocation()
  const { isOnboardingActive, currentStep } = useOnboarding()

  return (onboardingId: string) => {
    if (!isOnboardingActive || !currentStep) return false
    const stepPage = currentStep.page || location
    return HREF_TO_ONBOARDING[stepPage] === onboardingId
  }
}

function isItemActive(location: string, href: string) {
  return location === href || (href !== "/" && location.startsWith(href))
}

export function Sidebar({ className, collapsed: externalCollapsed, onCollapsedChange }: SidebarProps) {
  const [location] = useLocation()
  const [internalCollapsed] = useState(false)
  const collapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed
  const { user } = useAuth()
  const isNavHighlighted = useNavHighlight()
  const badges = useAssessmentBadges()
  const groups = visibleGroupsFor(user)

  return (
    <div
      className={cn(
        "pb-12 fixed left-0 top-[65px] h-[calc(100vh-65px)] bg-background border-r border-border transition-all duration-300 z-30 overflow-y-auto",
        collapsed ? "w-16" : "w-64",
        className,
      )}
      data-onboarding="sidebar-navigation"
    >
      <div className="space-y-2 py-3">
        {groups.map((group, idx) => (
          <div
            key={group.title}
            className={cn("transition-all duration-300", collapsed ? "px-1" : "px-3")}
          >
            <div className={cn(idx > 0 && "border-t border-border pt-2")}>
              <div
                className={cn(
                  "px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 whitespace-nowrap overflow-hidden transition-opacity duration-300",
                  collapsed ? "opacity-0 h-0 mb-0" : "opacity-100",
                )}
              >
                {group.title}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.href}
                    item={item}
                    active={isItemActive(location, item.href)}
                    highlighted={isNavHighlighted(item.onboardingId)}
                    collapsed={collapsed}
                    badges={badges}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function MobileSidebar({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [location] = useLocation()
  const { user } = useAuth()
  const isNavHighlighted = useNavHighlight()
  const badges = useAssessmentBadges()
  const groups = visibleGroupsFor(user)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="p-0 w-72 overflow-y-auto">
        <div className="space-y-3 py-4">
          {groups.map((group, idx) => (
            <div key={group.title} className="px-3">
              <div className={cn(idx > 0 && "border-t border-border pt-4")}>
                <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {group.title}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.href}
                      item={item}
                      active={isItemActive(location, item.href)}
                      highlighted={isNavHighlighted(item.onboardingId)}
                      mobile
                      badges={badges}
                      onNavigate={() => onOpenChange(false)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  )
}
